import * as yup from 'yup';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _keys from 'lodash/keys';
import _values from 'lodash/values';
import { BANNER_CTA_TYPE, BANNER_TYPE } from 'commons/models/banner.model';
import { APP_LANGUAGE, TOURNAMENT_SEGMENT_INSTRUCTION } from 'commons/models/common';
import {
	Row,
	RenderConfig,
	Column,
	TextInput,
	SelectInput,
	EMPTY_ARRAY,
	CASE,
	NumberInput,
	BOOLEAN_OPTIONS,
	ASSET_TYPE,
	AssetUploadInputOnS3
} from 'commons-ui';

import { ZUPEE_GAMES } from 'pages/dashboard/tournamentV2/utils/constants';
import { ScreenName } from 'commons/models/referral';
import {
	TYPE_OPTIONS,
	LOCATION_OPTIONS,
	CTA_TYPE_OPTIONS,
	CTA_VALUE_OPTIONS,
	LANG_OPTIONS,
	SEGMENT_INSTRUCTION_OPTIONS,
	EBannerLocation
} from './constants';

export const setValueUndefined = (props: any) => {
	const { name, setValue } = props;
	setValue(name, undefined);
};

export const ctaValueDependencyResolver = (
	props: any,
	dependencyValues: string[]
): {
	disabled: boolean;
	InputComponent?: typeof SelectInput | typeof TextInput;
} | null => {
	const ctaType = _get(dependencyValues, '0');
	const { InputComponent } = props;
	const { disabled } = props;

	if ((ctaType === undefined || ctaType === null) && !disabled) {
		setValueUndefined(props);
		return { disabled: true };
	}
	if (ctaType === BANNER_CTA_TYPE.IN_APP && InputComponent !== SelectInput) {
		return { InputComponent: SelectInput, disabled: false };
	}
	if (InputComponent !== TextInput) {
		return { InputComponent: TextInput, disabled: false };
	}

	return null;
};

export const ctaParamsGameNameDependencyResolver = (
	props: any,
	dependencyValues: any
): {
	disabled: boolean;
	InputComponent?: typeof SelectInput | typeof TextInput;
} => {
	const ctaType = _get(dependencyValues, '0');
	const { disabled } = props;

	if ((ctaType === undefined || ctaType === null) && !disabled) {
		return { disabled: true };
	}
	if (ctaType === BANNER_CTA_TYPE.IN_APP) {
		return { InputComponent: SelectInput, disabled: false };
	}

	setValueUndefined(props);

	return { disabled: true };
};

function isStringArray(seg: string[] | string): string[] | boolean {
	try {
		if (_isEmpty(seg)) {
			return false;
		}

		let segArr: string[] = seg as string[];

		if (typeof seg === 'string') {
			segArr = seg.split(',');
		}
		if (_isArray(segArr) && segArr.length > 0) {
			return segArr;
		}
		return false;
	} catch (error) {
		return false;
	}
}

export const segmentInsDependencyResolver = (
	props: any,
	dependencyValues: any
): {
	disabled: boolean;
	InputComponent?: typeof SelectInput;
} => {
	const segments = _get(dependencyValues, '0');

	if (isStringArray(segments)) {
		return { InputComponent: SelectInput, disabled: false };
	}

	setValueUndefined(props);

	return { disabled: true };
};

export const FORM_FIELD_KEYS = {
	CODE: 'code',
	LABEL: 'label',
	TYPE: 'type',
	LOCATION: 'location',
	CTA: 'cta',
	CTA_TYPE: 'cta_type',
	CTA_VALUE: 'cta_value',
	CTA_SELECT_VALUE: 'cta_select_value',
	CTA_PARAMS: 'cta_params',
	CTA_PARAMS_GAMENAME: 'cta_params_gamename',
	IMAGE_RATIO: 'imageRatio',
	LANGUAGE: 'language',
	SORT_ORDER: 'sortOrder',
	IMAGE_URL: 'imageUrl',
	EXPIRES_AT: 'expiresAt',
	IS_ACTIVE: 'isActive',
	EXPERIMENT: 'experiment',
	DISABLE_FOR_BLOCKED_USER: 'disabled_for_blocked_user',
	USER_SPECIFIC_SEGMENTS_LIST: 'userSpecificSegmentsList',
	SEGMENT_INSTRUCTION: 'segmentInstruction',
	MAX_APP_VERSION_SUPPORTED: 'maxAppVersionSupported',
	MIN_APP_VERSION_SUPPORTED: 'minAppVersionSupported'
};

export const validationSchema = yup.object().shape({
	code: yup.string().required('Code is a required field'),
	type: yup
		.string()
		.oneOf(_keys(BANNER_TYPE), 'Banner Type should be one of PROMOTIONAL, SALES or MISCELLANEOUS')
		.required('Banner Type is a required field'),
	cta: yup
		.object()
		.shape({
			type: yup
				.string()
				.oneOf(_keys(BANNER_CTA_TYPE), 'CTA Type should be one of IN_APP, DEEP_LINK, EXTERNAL_LINK, WEBVIEW_LINK'),
			value: yup.string().when('cta.type', {
				is: (value: string[]) => value != null,
				then: yup.string().oneOf(_keys(ScreenName)).required()
			}),
			params: yup.object().shape({
				gameName: yup.string().uppercase()
			})
		})
		.optional(),
	expiresAt: yup
		.number()
		.transform((value, originalValue) => {
			if (originalValue === '' || originalValue === null) {
				return undefined;
			}
			return originalValue;
		})
		.test({
			message: 'Expiration date should be a valid date',
			test: (value: any) => value !== '' || value !== null || !Number.isNaN(new Date(value).getTime())
		})
		.test({
			message: 'Expiration date should be greater than current time',
			test: (value: any) => new Date(value) > new Date()
		})
		.required('Expiration date is a required field'),
	label: yup.string().required('Label is a required field'),
	imageUrl: yup.string().required('Banner can not be created without the image is a required field'),
	sortOrder: yup.number().required('Sort order is a required field'),
	location: yup
		.string()
		.oneOf(_keys(EBannerLocation), 'Location should be one of WALLET, REFERRAL or HOME_SCREEN')
		.required('Location is a required field'),
	language: yup
		.string()
		.oneOf(_values(APP_LANGUAGE), 'Language should be one of HINDI or ENGLISH')
		.required('Language is a required field'),
	maxAppVersionSupported: yup.number(),
	minAppVersionSupported: yup.number(),
	expName: yup.string(),
	removedForBlockedUser: yup.boolean(),
	userSpecificSegmentsList: yup
		.array()
		.of(yup.string().required('Each segment in the array must be a non-empty string'))
		.transform((value, originalValue) => {
			if (originalValue === '' || originalValue === null) {
				return undefined;
			}

			const newValue: string[] | boolean = isStringArray(originalValue);
			if (newValue) {
				return newValue;
			}
			return originalValue;
		}),
	segmentInstruction: yup.string().when('userSpecificSegmentsList', {
		is: (value: string[]) => value != null,
		then: yup.string().oneOf(_keys(TOURNAMENT_SEGMENT_INSTRUCTION)).required()
	})
});

export const FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS.CODE]: new RenderConfig()
		.setName('code')
		.setLabel('Code')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: false }, letterCase: CASE.UPPERCASE, required: true }),
	[FORM_FIELD_KEYS.LABEL]: new RenderConfig()
		.setName('label')
		.setLabel('Label')
		.setRenderer(TextInput)
		.setRenderProps({ isEditable: { create: true, edit: false }, required: true }),
	[FORM_FIELD_KEYS.TYPE]: new RenderConfig()
		.setName('type')
		.setLabel('Banner Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: false, edit: false },
			options: TYPE_OPTIONS,
			required: true
		}),
	[FORM_FIELD_KEYS.LOCATION]: new RenderConfig()
		.setName('location')
		.setLabel('Location')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: LOCATION_OPTIONS,
			required: true
		}),
	[FORM_FIELD_KEYS.CTA]: new RenderConfig().setName('cta').setLabel('CTA').setRenderProps({ required: false }),
	[FORM_FIELD_KEYS.CTA_TYPE]: new RenderConfig()
		.setName('type')
		.setLabel('Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: CTA_TYPE_OPTIONS
		}),
	[FORM_FIELD_KEYS.CTA_VALUE]: new RenderConfig()
		.setName('value')
		.setLabel('Value')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			options: CTA_VALUE_OPTIONS,
			dependencies: ['cta.type'],
			dependencyResolver: ctaValueDependencyResolver
		}),
	[FORM_FIELD_KEYS.CTA_PARAMS]: new RenderConfig().setName('params').setLabel('Params').setRenderProps({ required: false }),
	[FORM_FIELD_KEYS.CTA_PARAMS_GAMENAME]: new RenderConfig()
		.setName('gameName')
		.setLabel('gameName')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: ZUPEE_GAMES,
			dependencies: ['cta.type'],
			dependencyResolver: ctaParamsGameNameDependencyResolver
		}),
	[FORM_FIELD_KEYS.LANGUAGE]: new RenderConfig()
		.setName('language')
		.setLabel('Language')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: LANG_OPTIONS,
			required: true
		}),
	[FORM_FIELD_KEYS.SORT_ORDER]: new RenderConfig()
		.setName('sortOrder')
		.setLabel('Sort Order')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.IMAGE_URL]: new RenderConfig()
		.setName('imageUrl')
		.setLabel('Image')
		.setRenderer(AssetUploadInputOnS3)
		.setRenderProps({ isEditable: { create: true, edit: true }, required: true, assetType: ASSET_TYPE.IMAGE }),

	[FORM_FIELD_KEYS.EXPIRES_AT]: new RenderConfig()
		.setName('expiresAt')
		.setLabel('Expires At')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'datetime-local',
			format: 'yyyy-dd-mm HH:MM:ss',
			required: true
		}),
	[FORM_FIELD_KEYS.MIN_APP_VERSION_SUPPORTED]: new RenderConfig()
		.setName('minAppVersionSupported')
		.setLabel('Min App Version Supported')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.EXPERIMENT]: new RenderConfig()
		.setName('expName')
		.setLabel('Banner Experiment Name')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true }
		}),
	[FORM_FIELD_KEYS.DISABLE_FOR_BLOCKED_USER]: new RenderConfig()
		.setName('removedForBlockedUser')
		.setLabel('Hide Banner for Blocked User?')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS
		})
		.setToolTip('do not select if no need to hide banner for blocked users'),
	[FORM_FIELD_KEYS.MAX_APP_VERSION_SUPPORTED]: new RenderConfig()
		.setName('maxAppVersionSupported')
		.setLabel('Max App Version Supported')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.USER_SPECIFIC_SEGMENTS_LIST]: new RenderConfig()
		.setName('userSpecificSegmentsList')
		.setLabel('Banner Applicable Segment list')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true }
		})
		.setToolTip('add comma seperated segment list applicable for banner. eg: 1004,1005'),
	[FORM_FIELD_KEYS.SEGMENT_INSTRUCTION]: new RenderConfig()
		.setName('segmentInstruction')
		.setLabel('Segment Instruction')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: SEGMENT_INSTRUCTION_OPTIONS,
			dependencies: ['userSpecificSegmentsList'],
			dependencyResolver: segmentInsDependencyResolver
		})
};

export const FORM_LAYOUT_CONFIG = [
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.CODE)]), new Column([new Row(FORM_FIELD_KEYS.LABEL)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.TYPE)]),
		new Column([new Row(FORM_FIELD_KEYS.LANGUAGE)]),
		new Column([new Row(FORM_FIELD_KEYS.LOCATION)])
	]),
	new Row(
		FORM_FIELD_KEYS.CTA,
		EMPTY_ARRAY,
		[new Column([new Row(FORM_FIELD_KEYS.CTA_TYPE)]), new Column([new Row(FORM_FIELD_KEYS.CTA_VALUE)])],
		[new Row(FORM_FIELD_KEYS.CTA_PARAMS, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.CTA_PARAMS_GAMENAME)])])]
	),
	new Row(FORM_FIELD_KEYS.IMAGE_URL),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.MIN_APP_VERSION_SUPPORTED)]),
		new Column([new Row(FORM_FIELD_KEYS.MAX_APP_VERSION_SUPPORTED)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.EXPERIMENT)]),
		new Column([new Row(FORM_FIELD_KEYS.DISABLE_FOR_BLOCKED_USER)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.USER_SPECIFIC_SEGMENTS_LIST)]),
		new Column([new Row(FORM_FIELD_KEYS.SEGMENT_INSTRUCTION)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.SORT_ORDER)]), new Column([new Row(FORM_FIELD_KEYS.EXPIRES_AT)])])
];

export const getBannerFormDefaults = (): { type: BANNER_TYPE } => {
	return {
		type: BANNER_TYPE.PROMOTIONAL
	};
};
